export enum ValuationMethod {
  AVM = 'avm',
  COST_BASED_INTRINSIC = 'cost_based_intrinsic',
  COST_BASED_DEVELOPER = 'cost_based_developer',
  // NOTE: Income-based methods are temporarily hidden from UI (needs refactoring)
  INCOME_BASED_STATIC = 'income_based_static',
  INCOME_BASED_DYNAMIC = 'income_based_dynamic',
  COMPARABLE_SALES = 'comparable_sales',
  CUSTOM = 'custom',
}

export enum ValuationSource {
  POPETY_IO = 'popety_io',
  PRICE_HUBBLE = 'price_hubble',
  WUEST_PARTNER = 'wuest_partner',
  MANUAL = 'manual',
  LISTINGS_COMPARABLES = 'listings_comparables',
  TRANSACTIONS_COMPARABLES = 'transactions_comparables',
}

export interface ValuationRange {
  min?: number
  max?: number
}

export interface ValuationMeta {
  [key: string]: string | number
}

export interface Valuation {
  id: string
  name: string
  method: ValuationMethod
  source: ValuationSource
  value: number
  range?: ValuationRange
  confidence?: number // 1-3 scale
  description: string
  meta: ValuationMeta
  color?: string // Hex color for chart display (e.g., #F38181)
  createdAt?: Date
  updatedAt?: Date
  isVisible: boolean
  isEditable: boolean
}

export interface FinalValuation {
  value?: number
  range?: ValuationRange
}

// Form Data Types
export interface ValuationFormData {
  name: string
  description?: string
  meta: Record<string, string>
}

export interface IntrinsicValueFormData extends ValuationFormData {
  // Land Value (totals; landPrice is the weighted average when landSegments is set)
  landArea: number
  landPrice: number
  landSegments?: LandSegment[]

  // Multiple Buildings (each with cost method, size, and depreciation)
  buildings: ValuationBuilding[]

  // Depreciation method (global - applies to all buildings)
  depreciationMethod: 'linear' | 'ross'

  // Ross-Heidecke method (global settings for condition assessment)
  agePercent?: string
  buildingCondition?: string

  // Additional Elements (garages, pools, landscaping, etc.)
  additionalElements: AdditionalElement[]
}

// One priced part of the parcel (usually one zone of land_use_plans_general)
export interface LandSegment {
  id: string
  label: string
  area: number // m²
  price: number // CHF/m²
  buildable: boolean
  source: 'std' | 'zone' | 'manual'
  coverPerc?: number // 0–1, share of the parcel from land_use_plans_general
}

export interface AdditionalElement {
  id: string
  name: string
  value: number
}

export interface ValuationBuilding {
  id: string
  name: string
  costMethod: 'volume' | 'surface'

  // Volume-based
  buildingVolume?: number
  constructionTypeVolume?: string
  costPerVolume?: number

  // Surface-based
  livingArea?: number
  qualityLevel?: string
  costPerSurface?: number

  // Depreciation (per building)
  buildingAge: number
  expectedLifetime: number

  // Calculated values (stored for display)
  replacementCost?: number
  depreciationRate?: number
  depreciationAmount?: number
}

export type DeveloperIndexType = 'ius' | 'ibus' | 'im' | 'custom'

export interface DeveloperMethodFormData extends ValuationFormData {
  // Development Overview
  plotArea: number

  // Index selection (replaces generic FAR)
  indexType: DeveloperIndexType
  indexValue: number

  // Legacy field - keeping for backward compatibility
  far?: number

  developmentType: string
  developmentPeriod: number

  // Revenue Model
  revenueModel: 'sale' | 'rental'

  // Sale Model (legacy - unit-by-unit entry)
  units: UnitType[]

  // Simplified Revenue Model (new - average price per m²/m³)
  pricePerUnit?: number // CHF/m² or CHF/m³ depending on index type

  // Rental Model
  annualRent?: number
  marketYield?: number

  // Hard Costs
  demolitionCost: number
  sitePrep: number
  constructionCost: number
  externalWorks: number

  // Soft Costs (percentage-based)
  planningCost: number // % of construction costs
  legalCost: number // % of construction costs
  marketingCost: number // % of GDV
  contingency: number // % of construction costs

  // Finance
  interestRate: number

  // Profit
  profitMargin: number

  // Simplified form flag (to distinguish between old and new form data)
  isSimplifiedForm?: boolean
}

export interface UnitType {
  id: string
  type: string
  count: number
  pricePerSqm: number
  size: number
}

export interface StaticYieldFormData extends ValuationFormData {
  propertyType: string
  rentalUnits: RentalUnit[]

  // Vacancy and Collection Loss
  vacancyRate: number
  collectionLoss: number

  // Operating Expenses
  managementFees: number
  maintenance: number
  insurance: number
  propertyTax: number
  utilities: number
  reserves: number
  buildingValue: number

  // Capitalization Rate
  capRateMethod: 'market' | 'buildup' | 'direct'
  capitalizationRate: number

  // Build-up method components (optional)
  riskFreeRate?: number
  locationRisk?: number
  propertyRisk?: number
  tenantRisk?: number
  managementBurden?: number
  growthExpectation?: number
}

export interface RentalUnit {
  id: string
  description: string
  area: number
  monthlyRent: number
}

export interface DCFFormData extends ValuationFormData {
  // Base Income
  initialRent: number
  rentGrowthRate: number
  projectionPeriod: number

  // Operating Expenses
  vacancyRate: number
  managementCosts: number
  maintenanceCosts: number
  insurance: number
  propertyTax: number
  utilities: number
  reserves: number
  expenseGrowthRate?: number

  // Discount and Terminal
  discountRate: number
  exitCapRate: number

  // Capital Expenditures (optional)
  majorCapex?: CapitalExpenditure[]
}

export interface CapitalExpenditure {
  id: string
  year: number
  description: string
  amount: number
}

export interface CustomValuationFormData extends ValuationFormData {
  methodology: string // Key factors considered (full text)
  assumptions: string // Kept for backward compatibility, not used in UI
  calculations: CustomCalculation[]
  finalValue: number
  range?: {
    // Value range (min/max) - structured object
    min: number
    max: number
  }
  confidence?: number // Valuation confidence level (1-3 scale)
  valuationDate?: string // Date when valuation was performed
  valuer?: string // Person or company who performed the valuation
  color?: string // Hex color for chart display (e.g., #F38181)
}

export interface CustomCalculation {
  id: string
  description: string
  value: number
  formula?: string
}

// Add Valuation interface types for the form
export type ValuationMethodCategory = 'cost' | 'income' | 'manual'

export interface ValuationMethodInfo {
  id: string
  name: string
  nameKey?: string
  category: ValuationMethodCategory
  badge: string
  badgeClass: string
  description: string
  descriptionKey?: string
  method: ValuationMethod
}

export interface ValuationCategory {
  id: string
  title: string
  titleKey?: string
  description: string
  descriptionKey?: string
  iconName: string
  methods: ValuationMethodInfo[]
}

export interface AddValuationFormData {
  method?: ValuationMethod
  category?: ValuationMethodCategory
  name?: string
  description?: string
  // Specific form fields will be added based on method
  [key: string]: any
}

export interface AddValuationState {
  currentStep: 'selection' | 'form'
  selectedMethod: ValuationMethodInfo | null
  formData: AddValuationFormData
  isLoading: boolean
  error: string | null
}

export interface AddValuationContextValue extends AddValuationState {
  editingValuation?: Valuation | null
  selectMethod: (method: ValuationMethodInfo) => void
  backToSelection: () => void
  updateFormData: (data: Partial<AddValuationFormData>) => void
  saveAsDraft: () => Promise<void>
  saveValuation: (dataOverride?: Partial<AddValuationFormData>) => Promise<void>
  cancelAddValuation: () => void
  resetState: () => void
}
